import { APIService } from 'src/app/service/api.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  HostInfo,
  HostInfoSearchDto,
  HostInfoUpdateDto,
  HostSettingsUpdateDto,
  HostSettingsView,
  Influencer,
  User,
  UserAddPointDto,
  UserSetting,
} from 'src/app/model/user';
import { HostDailySummary } from '../model/host-daily-summary';
import { Pagination, PaginationSearchDto } from '../model/pagination';
import { EarnPointType, UserPointHistory } from '../model/user';
import { UserCreateDto, UserSearchDto, UserUpdateDto } from './../model/user';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private apiService: APIService,
    private authService: AuthService,
  ) {}

  search(dto: UserSearchDto): Observable<Pagination<User>> {
    return this.apiService.post<Pagination<User>>(`/api/users/search`, dto);
  }

  signup(user: UserCreateDto): Observable<User> {
    return this.apiService.post<User>(`/api/users`, user);
  }

  get(nid: number): Observable<User> {
    return this.apiService.get<User>(`/api/users/${nid}`);
  }

  getByUserCode(userCode: string): Observable<User> {
    return this.apiService.get<User>(`/api/users/userCode/${userCode}`);
  }

  getProfile(nid: number): Observable<User> {
    return this.apiService.get<User>(`/api/users/profile/user/${nid}`);
  }

  getProfileWithUserInfo(nid: number): Observable<User> {
    return this.apiService.get<User>(`/api/users/profile/user/${nid}/userInfo`);
  }

  getFollowings(id: number): Observable<any[]> {
    return this.apiService.get<any[]>(`/api/users/followings/list/${id}`);
  }

  getFollowers(id: number): Observable<any[]> {
    return this.apiService.get<any[]>(`/api/users/followers/list/${id}`);
  }

  async editAndUpdateToken(userId, dto: UserUpdateDto): Promise<User> {
    const user = await this.apiService.put<User>(`/api/users/${userId}`, dto).toPromise();
    await this.authService.updateToken(userId).toPromise();
    return user;
  }

  likeProduct(productId: number, isLike: boolean) {
    if (isLike) {
      this.authService.updateUserInfo({ isLikeProductRead: false }).subscribe();
    }
    return this.apiService.post(`/api/users/likes/product`, {
      id: productId,
      isLike,
    });
  }

  likeUser(userId: number, isLike: boolean) {
    return this.apiService.post(`/api/users/likes/user`, {
      id: userId,
      isLike,
    });
  }

  likeFeed(feedId: number, isLike: boolean): Observable<any> {
    return this.apiService.post<any>(`/api/users/likes/feed`, {
      id: feedId,
      isLike,
    });
  }

  findPassword(email: string): any {
    return this.apiService.post(`/auth/find-password`, { email });
  }

  resetPassword(validationCode: string, password: string): any {
    return this.apiService.put(`/auth/reset-password`, {
      validationCode,
      password,
    });
  }

  changePassword(data: any): Observable<any> {
    return this.apiService.put(`/api/users/password`, data);
  }

  searchPointHistory(page: number, limit: number): Observable<Pagination<UserPointHistory>> {
    if (!page) page = 1;
    return this.apiService.get<Pagination<UserPointHistory>>(`/api/users/point/history?page=${page}&limit=${limit}`);
  }

  getPointStatisticsByType(): Observable<{ type: EarnPointType; value: number }[]> {
    return this.apiService.get<{ type: EarnPointType; value: number }[]>(`/api/users/point/history/statistics`);
  }

  getPointTopPercent(): Observable<{ percent: number }> {
    return this.apiService.get<{ percent: number }>(`/api/users/point/history/top-percent`);
  }

  getSettings(): Observable<UserSetting> {
    return this.apiService.get<UserSetting>('/api/settings');
  }

  setSettings(unreadAlarm: boolean): Observable<UserSetting> {
    return this.apiService.put<UserSetting>('/api/settings', { unreadAlarm });
  }

  update(userId: number, dto: UserUpdateDto): Observable<User> {
    return this.apiService.put<User>(`/api/users/${userId}`, dto);
  }

  updatePhoneNumber(userId: number, phoneNumber: string): Observable<User> {
    return this.apiService.put<User>(`/api/users/phoneNumber/${userId}`, {
      phoneNumber,
    });
  }

  updateRole(userId: number, dto: UserUpdateDto): Observable<User> {
    return this.apiService.put<User>(`/api/users/${userId}/role`, dto);
  }

  updateUserCode(userCode: string): Observable<User> {
    return this.apiService.put<User>(`/api/users/userCode/checkAndUpdate`, {
      userCode,
    });
  }

  getHostInfoByCurrent(): Observable<HostInfo> {
    return this.apiService.get<HostInfo>(`/api/hostInfo`);
  }

  /** admin이 host_info.id로 직접 호스트 정보 조회 (호스트 상세 페이지용) */
  getHostInfoByIdAsAdmin(hostInfoId: number): Observable<HostInfo> {
    return this.apiService.get<HostInfo>(`/api/hostInfo/${hostInfoId}`);
  }

  searchHostInfo(dto: HostInfoSearchDto): Observable<Pagination<HostInfo>> {
    return this.apiService.post<Pagination<HostInfo>>(`/api/hostInfo/search`, dto);
  }

  updateHostInfo(userId: number, dto: HostInfoUpdateDto) {
    return this.apiService.put(`/api/hostInfo/${userId}`, dto);
  }

  /** 호스트 본인의 host_info.mobile 인증 요청 */
  requestHostInfoMobileSms(phoneNumber: string): Observable<boolean> {
    return this.apiService.post<boolean>(`/api/hostInfo/me/mobile/sms`, { phoneNumber });
  }

  /** 호스트 본인의 host_info.mobile 인증 확인 */
  checkHostInfoMobileSms(phoneNumber: string, authNumber: string): Observable<boolean> {
    return this.apiService.post<boolean>(`/api/hostInfo/me/mobile/sms/check`, { phoneNumber, authNumber });
  }

  /** admin 인라인 mobile 수정 */
  updateHostInfoMobileAsAdmin(userId: number, mobile: string | null): Observable<HostInfo> {
    return this.apiService.put<HostInfo>(`/api/hostInfo/${userId}/mobile`, { mobile });
  }

  /** 호스트 본인의 일별 예약 요약 (KST date) */
  getHostDailySummary(date: string): Observable<HostDailySummary> {
    return this.apiService.get<HostDailySummary>(`/api/hostInfo/daily-summary?date=${encodeURIComponent(date)}`);
  }

  /** 호스트 본인의 운영 설정 조회 — row 없으면 default view를 반환 */
  getHostSettings(): Observable<HostSettingsView> {
    return this.apiService.get<HostSettingsView>(`/api/host-settings/me`);
  }

  /** 호스트 본인의 운영 설정 부분 갱신 */
  updateHostSettings(dto: HostSettingsUpdateDto): Observable<HostSettingsView> {
    return this.apiService.patch<HostSettingsView>(`/api/host-settings/me`, dto);
  }

  getLikeProductCount(): Observable<number> {
    return this.apiService.get<number>(`/api/users/likes/product/count`);
  }

  setUserTypeTestAnswer(answers: string, recommendCode?: string) {
    return this.apiService.post(`/api/users/typeTest`, {
      answers,
      recommendCode,
    });
  }

  getTypeTestAnswer() {
    return this.apiService.get(`/api/users/typeTest/answer`);
  }

  getParticipantCount(): Observable<number> {
    return this.apiService.get<number>(`/api/users/typeTest/participantCount`);
  }

  getDonationCount(): Observable<{ count: number }> {
    return this.apiService.get<{ count: number }>(`/api/users/typeTest/donationCount`);
  }

  getMyDonationCount(): Observable<number> {
    return this.apiService.get<number>(`/api/users/typeTest/my/donationCount`);
  }

  getDonationRankList(): Observable<{ count: number; photo: string; nickname: string }[]> {
    return this.apiService.get<{ count: number; photo: string; nickname: string }[]>(
      `/api/users/typeTest/donationRank`,
    );
  }

  searchInfluencers(searchDto: PaginationSearchDto): Observable<Pagination<Influencer>> {
    return this.apiService.get<Pagination<Influencer>>('/api/users/influencers/list', searchDto);
  }

  addPoint(dto: UserAddPointDto) {
    return this.apiService.post('/api/users/addPoint', dto);
  }
}
